import { inject, Injectable, signal } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { Intervention, InterventionEvaluationResult, InterventionReferential, PostIntervention, PutIntervention } from 'src/app/interfaces/api/intervention';
import { InterventionService } from 'src/app/services/api/interventions/intervention.service';

export interface InterventionFilters {
    category?: string;
    subType?: string;
    applicant?: string;
    assignee?: string;
    dateFrom?: string;
    dateTo?: string;
    // 'NON_EVALUEE' is a frontend-only sentinel (no evaluation.result stored yet) — not a real
    // persisted value, unlike the three InterventionEvaluationResult codes.
    evaluationResult?: InterventionEvaluationResult | 'NON_EVALUEE' | '';
    search?: string;
}

const defaultFilters: InterventionFilters = {};

/**
 * Store centralisé pour l'état des interventions.
 * Le tableau est paginé/filtré côté serveur (GET .../interventions/searchAndCount) : ce store
 * ne garde en mémoire que la page courante, pas la collection complète.
 */
@Injectable({
    providedIn: 'root'
})

export class InterventionStore {
    // Services
    private readonly interventionService: InterventionService = inject(InterventionService);

    // Signals
    private _results = signal<Intervention[]>([]);
    readonly results = this._results.asReadonly();

    private _total = signal<number>(0);
    readonly count = this._total.asReadonly();

    private _referential = signal<InterventionReferential>({});
    readonly referential = this._referential.asReadonly();

    private _referentialError = signal<string | null>(null);
    readonly referentialError = this._referentialError.asReadonly();

    private _loading = signal<boolean>(false);
    readonly loading = this._loading.asReadonly();

    private _error = signal<string | null>(null);
    readonly error = this._error.asReadonly();

    private _filters = signal<InterventionFilters>({ ...defaultFilters });
    readonly filters = this._filters.asReadonly();

    private _pageIndex = signal<number>(0);
    readonly pageIndex = this._pageIndex.asReadonly();

    private _pageSize = signal<number>(20);
    readonly pageSize = this._pageSize.asReadonly();

    private _sortActive = signal<string>('createdDate');
    private _sortDirection = signal<'asc' | 'desc' | ''>('desc');

    private _selectedId = signal<string | null>(null);
    readonly selectedId = this._selectedId.asReadonly();

    private currentProjectId: string | null = null;
    private adminMode: boolean = false;
    private loadSubscription: Subscription | null = null;
    private socketSubscription: Subscription | null = null;
    private readonly refresh$ = new Subject<void>();

    constructor() {
        this.refresh$.pipe(debounceTime(150)).subscribe(() => this.load());
    }

    // ----------- LOAD ----------- //

    isProjectLoaded(projectId: string): boolean {
        return this.currentProjectId === projectId;
    }

    loadInterventions(projectId: string): void {
        if (!projectId) return;

        const modeChanged = this.adminMode;
        this.adminMode = false;

        const projectChanged = this.currentProjectId !== projectId;
        this.currentProjectId = projectId;

        if (modeChanged || projectChanged) {
            this._filters.set({ ...defaultFilters });
            this._pageIndex.set(0);
        }

        if (projectChanged) {
            this.interventionService.primeSyncState();
            this.socketSubscription?.unsubscribe();
            this.socketSubscription = this.interventionService.watchChanges().subscribe(() => this.refresh$.next());
        }

        this.load();
    }

    loadAllInterventions(): void {
        const modeChanged = !this.adminMode;
        this.adminMode = true;
        this.currentProjectId = null;
        this.socketSubscription?.unsubscribe();

        if (modeChanged) this._filters.set({ ...defaultFilters });
        this._pageIndex.set(0);
        this.load();
    }

    setPage(pageIndex: number, pageSize: number): void {
        this._pageIndex.set(pageIndex);
        this._pageSize.set(pageSize);
        this.load();
    }

    setSort(active: string, direction: 'asc' | 'desc' | ''): void {
        this._sortActive.set(active);
        this._sortDirection.set(direction);
    }

    private load(): void {
        const projectId = this.currentProjectId;
        if (!this.adminMode && !projectId) return;

        this.loadSubscription?.unsubscribe();
        this._loading.set(true);
        this._error.set(null);

        const filters = this._filters();
        const query: Record<string, string | number> = {
            offset: this._pageIndex() * this._pageSize(),
            limit: this._pageSize(),
        };

        if (projectId) query['project'] = projectId;

        if (filters.search?.trim()) query['q'] = filters.search.trim();
        if (filters.category) query['category'] = filters.category;
        if (filters.subType) query['subType'] = filters.subType;
        if (filters.applicant) query['applicant'] = filters.applicant;
        if (filters.assignee) query['assignee'] = filters.assignee;
        if (filters.dateFrom) query['dateFrom'] = filters.dateFrom;
        if (filters.dateTo) query['dateTo'] = filters.dateTo;
        if (filters.evaluationResult) query['evaluationResult'] = filters.evaluationResult;

        const sortActive = this._sortActive();
        const sortDirection = this._sortDirection();
        if (sortActive && sortDirection) {
            query['sort'] = sortDirection === 'desc' ? `-${sortActive}` : sortActive;
        }

        const search$ = this.adminMode
            ? this.interventionService.searchAndCountAllInterventions(query)
            : this.interventionService.searchAndCountInterventions(query);

        this.loadSubscription = search$.subscribe({
            next: (res) => {
                const results = Array.isArray(res?.results) ? res.results : [];
                this._results.set(results);
                this._total.set(typeof res?.total === 'number' ? res.total : results.length);
                this._loading.set(false);
            },
            error: (err) => {
                this._error.set(err instanceof Error ? err.message : 'Failed to load interventions');
                this._loading.set(false);
            }
        });
    }

    loadReferential(): void {
        this._referentialError.set(null);

        this.interventionService.getInterventionReferential().subscribe({
            next: (referential) => {
                const isPlainObject = typeof referential === 'object' && referential !== null && !Array.isArray(referential);

                if (!isPlainObject) {
                    console.warn('[InterventionStore] Unexpected referential payload, expected a category -> subTypes map:', referential);
                    this._referential.set({});
                    this._referentialError.set('malformed');
                    return;
                }

                this._referential.set(referential);
                this._referentialError.set(Object.keys(referential).length ? null : 'empty');
            },
            error: (err) => {
                console.error('[InterventionStore] Failed to load intervention referential (GET intervention-referential):', err);
                this._referential.set({});
                this._referentialError.set('request_failed');
            }
        });
    }

    // ----------- ITEM ----------- //

    fetchIntervention(id: string): Observable<Intervention> {
        return this.adminMode ? this.interventionService.getAllIntervention(id) : this.interventionService.getIntervention(id);
    }

    // ----------- CRUD ----------- //

    addIntervention(intervention: PostIntervention): Observable<Intervention> {
        return this.interventionService.addIntervention(intervention).pipe(tap(() => this.load()));
    }

    updateIntervention(intervention: PutIntervention): Observable<Intervention> {
        const update$ = this.adminMode
            ? this.interventionService.updateAllIntervention(intervention)
            : this.interventionService.updateIntervention(intervention);

        return update$.pipe(tap(() => this.load()));
    }

    removeIntervention(id: string): Observable<any> {
        const remove$ = this.adminMode
            ? this.interventionService.removeAllIntervention(id)
            : this.interventionService.removeIntervention(id);

        return remove$.pipe(
            tap(() => {
                if (this._selectedId() === id) this._selectedId.set(null);
                this.load();
            })
        );
    }

    // ----------- SELECTION / FILTERS ----------- //

    select(id: string | null): void {
        this._selectedId.set(id);
    }

    setFilters(filters: Partial<InterventionFilters>): void {
        const next = { ...this._filters(), ...filters };
        if (this._pageIndex() === 0 && this.filtersEqual(next, this._filters())) return;

        this._filters.set(next);
        this._pageIndex.set(0);
        this.load();
    }

    resetFilters(): void {
        if (this._pageIndex() === 0 && this.filtersEqual(defaultFilters, this._filters())) return;

        this._filters.set({ ...defaultFilters });
        this._pageIndex.set(0);
        this.load();
    }

    private filtersEqual(a: InterventionFilters, b: InterventionFilters): boolean {
        const keys: (keyof InterventionFilters)[] = [
            'category', 'subType', 'applicant', 'assignee', 'dateFrom', 'dateTo', 'evaluationResult', 'search',
        ];
        return keys.every((key) => (a[key] ?? '') === (b[key] ?? ''));
    }

    reset(): void {
        this.loadSubscription?.unsubscribe();
        this.socketSubscription?.unsubscribe();
        this._results.set([]);
        this._total.set(0);
        this._loading.set(false);
        this._error.set(null);
        this._referential.set({});
        this._referentialError.set(null);
        this._filters.set({ ...defaultFilters });
        this._pageIndex.set(0);
        this._sortActive.set('createdDate');
        this._sortDirection.set('desc');
        this._selectedId.set(null);
        this.currentProjectId = null;
        this.adminMode = false;
    }
}